/**
 * A short uppercase label on a small plate.
 *
 *   needsYou  gold — a count that wants action ("2 idle")
 *   accent    purple — a rank or a milestone ("#1 of 2", "Q9 complete")
 *   you       the small purple YOU tag beside your own name
 *   neutral   anything else worth a plate
 *   danger    coral — what costs you where it should not: an aircraft that
 *             overflows an airport's positions ("Overflow · LAX")
 *   hue       a colour known only at runtime (`hue`, as `#rrggbb`) — an
 *             airline's, say — drawn with the same recipe as the purple chip
 *
 * A runtime colour arrives as the `--hue` custom property rather than a
 * class, and a chip in one must still say what it stands for in its text:
 * colours repeat, so colour alone never identifies anything.
 */

import type { CSSProperties, HTMLAttributes } from 'react';

import utilities from '../../styles/utilities.module.css';
import styles from './chip.module.css';

const TONE = {
  needsYou: styles.needsYou,
  accent: styles.accent,
  you: styles.you,
  neutral: styles.neutral,
  danger: styles.danger,
} as const;

type ChipProps = Omit<HTMLAttributes<HTMLSpanElement>, 'style'> &
  (
    | { tone: keyof typeof TONE; hue?: never }
    /** `hue` as `#rrggbb`. */
    | { tone: 'hue'; hue: string }
  );

export function Chip({ tone, hue, className = '', ...rest }: ChipProps) {
  const toneClass = tone === 'hue' ? utilities.hueTint : TONE[tone];
  const hueStyle = tone === 'hue' ? ({ '--hue': hue } as CSSProperties) : undefined;
  return <span className={[styles.chip, toneClass, className].filter(Boolean).join(' ')} style={hueStyle} {...rest} />;
}
